import React, { useState, useRef } from 'react';
import { useFloating, autoUpdate, flip, shift, offset as offsetFunc, arrow } from '@floating-ui/react-dom';
import { PopoverProps } from './types';
import { PopoverContext } from './PopoverContext';

const Popover = ({
  children,
  isOpen,
  initialOpen = false,
  position = 'top',
  offset = 4,
  onOpenChanged = () => undefined,
  triggerEvent = 'click',
  disableFlip = false,
}: PopoverProps) => {
  const [innerOpen, setInnerOpen] = useState(initialOpen);
  const arrowRef = useRef<HTMLDivElement>(null);
  const open = isOpen === undefined ? innerOpen : isOpen;
  const positionSide = position.split('-')[0];
  const isHorizontalPosition = positionSide === 'right' || positionSide === 'left';
  const arrowExists = arrowRef?.current;
  const arrowMiddleware = arrowExists ? arrow({ element: arrowRef, padding: 8 }) : undefined;
  const arrowOffset = arrowExists ? Math.sqrt(2 * arrowRef.current.offsetWidth ** 2) / 2 : 0;
  const flipMiddleware = disableFlip ? undefined : flip();

  const setOpen = (value: boolean) => {
    setInnerOpen(value);
    onOpenChanged(value);
  };

  const floatingData = useFloating({
    placement: position,
    open,
    whileElementsMounted: autoUpdate,
    middleware: [
      offsetFunc(offset + arrowOffset),
      shift({ mainAxis: !isHorizontalPosition }),
      flipMiddleware,
      arrowMiddleware,
    ],
  });

  return (
    <PopoverContext.Provider value={{ floatingData, setOpen, open, arrowRef, triggerEvent }}>
      {children}
    </PopoverContext.Provider>
  );
};

Popover.displayName = 'Popover';

export default Popover;
